import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

export default function AboutPage() {
  return (
    <>
      <Helmet>
        <title>Om Os - Brønshøj Ure & Guld</title>
        <meta
          name="description"
          content="Lær mere om Brønshøj Ure & Guld. Din lokale guldsmed og urmager i Brønshøj med over 35 års erfaring inden for ædelt håndværk og personlig service."
        />
        <meta property="og:title" content="Om Os - Brønshøj Ure & Guld" />
        <meta
          property="og:description"
          content="Lær mere om Brønshøj Ure & Guld. Din lokale guldsmed og urmager i Brønshøj med over 35 års erfaring inden for ædelt håndværk og personlig service."
        />
        <meta
          property="og:image"
          content="https://assets.auxodev.dk/assets/807b5052-bea6-494e-8947-b0c29529a13c/elegant-black-and-white-logo-design-2.png"
        />
      </Helmet>

      {/* Hero Header */}
      <section className="bg-[#0B132B] text-white pt-36 sm:pt-40 lg:pt-52 pb-16 sm:pb-20 border-b border-white/10 text-center">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 space-y-4">
          <span className="text-xs uppercase tracking-[0.3em] text-[#B5A897] font-semibold block">
            DIN LOKALE GULDSMED & URMAGER
          </span>
          <h1 className="font-serif text-3xl sm:text-5xl uppercase tracking-[0.2em] font-normal text-white">
            OM BRØNSHØJ URE & GULD
          </h1>
          <p className="text-xs sm:text-sm text-stone-300 max-w-2xl mx-auto uppercase tracking-widest font-sans leading-relaxed">
            Over 35 års erfaring i guldsmedebranchen i Brønshøj
          </p>
        </div>
      </section>

      {/* Hovedfortælling: Historie & Håndværk */}
      <section className="py-16 sm:py-24 bg-[#F9F8F6]">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            
            {/* Tekstkolonne */}
            <div className="lg:col-span-6 space-y-6">
              <div className="w-[1px] h-10 bg-[#B5A897]"></div>
              <span className="text-xs uppercase tracking-[0.25em] text-[#B5A897] font-semibold block">
                VORES HISTORIE & PASSION
              </span>
              <h2 className="font-serif text-2xl sm:text-4xl font-normal uppercase tracking-[0.15em] text-[#1A1A1A] leading-snug">
                TRADITIONELT HÅNDVÆRK & PERSONLIG SERVICE
              </h2>
              <div className="text-stone-700 text-sm sm:text-base leading-relaxed space-y-4 font-sans">
                <p>
                  Hos Brønshøj Ure & Guld har vi betjent vores kunder med faglig stolthed og dedikation i mere end 40 år. Vi kombinerer klassiske guldsmedetraditioner med moderne ekspertise inden for smykker og ure.
                </p>
                <p>
                  Vores forretning er bygget på tillid, ærlighed og nær kontakt til vores kunder. Hvad enten du søger et nyt smykke til en særlig mærkedag, ønsker at give et arvestykke nyt liv eller har brug for kyndig rådgivning, tager vi os altid god tid til at lytte til dine ønsker.
                </p>
              </div>

              <div className="pt-2 flex flex-wrap gap-4">
                <Link
                  to="/kontakt"
                  className="inline-block px-7 py-3.5 bg-[#0B132B] text-white hover:bg-[#1a2340] transition-colors text-xs tracking-[0.2em] uppercase font-medium"
                >
                  KONTAKT OS
                </Link>
                <Link
                  to="/guldsmedevaerksted"
                  className="inline-block px-7 py-3.5 border border-[#0B132B] text-[#0B132B] hover:bg-[#0B132B] hover:text-white transition-colors text-xs tracking-[0.2em] uppercase font-medium"
                >
                  SE VORES VÆRKSTED
                </Link>
              </div>
            </div>

            {/* Billedkolonne */}
            <div className="lg:col-span-6 grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <div className="overflow-hidden">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/807b5052-bea6-494e-8947-b0c29529a13c/img-20250925-wa0004-2.jpg"
                  alt="Guldsmedeværkstedet hos Brønshøj Ure & Guld"
                  className="w-full h-72 sm:h-96 object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="overflow-hidden sm:mt-8">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/807b5052-bea6-494e-8947-b0c29529a13c/img-20251007-wa0023-2-1024x768.jpg"
                  alt="Butikken hos Brønshøj Ure & Guld"
                  className="w-full h-72 sm:h-96 object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Værdier / Det vi står for - Rolig og overskuelig opdeling */}
      <section className="py-16 sm:py-20 bg-white border-y border-[#E2DDD3]">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Kasse 1: Salg af smykker & ure */}
            <div className="p-6 sm:p-7 bg-[#F9F8F6] border border-[#E2DDD3] flex flex-col justify-between text-left">
              <div>
                <h3 className="font-serif text-base sm:text-lg font-normal uppercase tracking-wider text-[#1A1A1A] mb-3">
                  SALG AF SMYKKER & URE
                </h3>
                <p className="text-xs sm:text-sm text-stone-600 leading-relaxed font-sans">
                  Vi tilbyder et bredt udvalg af guld, unikke smykker fra eget værksted og kvalitetsure. Kig forbi butikken for at se vores sortiment og finde noget helt særligt til en god pris.
                </p>
              </div>
            </div>

            {/* Kasse 2: Vi køber dit guld */}
            <div className="p-6 sm:p-7 bg-[#F9F8F6] border border-[#E2DDD3] flex flex-col justify-between text-left">
              <div>
                <h3 className="font-serif text-base sm:text-lg font-normal uppercase tracking-wider text-[#1A1A1A] mb-3">
                  VI KØBER DIT GULD
                </h3>
                <p className="text-xs sm:text-sm text-stone-600 leading-relaxed font-sans">
                  Har du gammelt guld, arvestykker eller smykker, du ikke bruger? Kom ind og få en uforpligtende vurdering af dine værdier. Vi giver altid en fair og høj dagspris.
                </p>
              </div>
            </div>

            {/* Kasse 3: Service & vedligeholdelse */}
            <div className="p-6 sm:p-7 bg-[#F9F8F6] border border-[#E2DDD3] flex flex-col justify-between text-left">
              <div>
                <h3 className="font-serif text-base sm:text-lg font-normal uppercase tracking-wider text-[#1A1A1A] mb-3">
                  SERVICE & VEDLIGEHOLDELSE
                </h3>
                <p className="text-xs sm:text-sm text-stone-600 leading-relaxed font-sans">
                  Dine smykker og ure fortjener professionel pleje. Vi tilbyder rens og polering af smykker samt batteriskift, trykprøvning og komplet service af dine ure.
                </p>
              </div>
            </div>

            {/* Kasse 4: Værkstedsreparationer */}
            <div className="p-6 sm:p-7 bg-[#F9F8F6] border border-[#E2DDD3] flex flex-col justify-between text-left">
              <div>
                <h3 className="font-serif text-base sm:text-lg font-normal uppercase tracking-wider text-[#1A1A1A] mb-3">
                  VÆRKSTEDS-<br />REPARATIONER
                </h3>
                <p className="text-xs sm:text-sm text-stone-600 leading-relaxed font-sans">
                  Har du et arvestykke eller et smykke, der skal repareres? Vores værksted klarer alt fra lodning af kæder til ændring af ringstørrelser og isætning af sten. Få et uforpligtende tilbud.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
